import React from 'react';
import { BarChart, Trash2, RefreshCw, Calendar } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных расчетов
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-cyan-500" />
          Сохраненные расчеты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты дат для быстрого доступа.
        </p>
      </div>
    );
  }

  // Получение иконки для типа расчета
  const getCalculationTypeIcon = (type) => {
    switch (type) {
      case 'standard':
        return <Calendar className="w-4 h-4 text-cyan-500" />;
      case 'working':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'add-subtract':
        return <Calendar className="w-4 h-4 text-green-500" />;
      case 'age':
        return <Calendar className="w-4 h-4 text-purple-500" />;
      default:
        return <Calendar className="w-4 h-4 text-gray-500" />;
    }
  };

  // Получение типа расчета на русском языке
  const getCalculationTypeName = (type) => {
    switch (type) {
      case 'standard':
        return 'Разница дат';
      case 'working':
        return 'Рабочие дни';
      case 'add-subtract':
        return 'Добавление/вычитание';
      case 'age':
        return 'Расчет возраста';
      default:
        return 'Расчет дат';
    }
  };

  // Форматирование даты сохранения
  const formatSaveDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-cyan-500" />
          Сохраненные расчеты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'запись' : 
            (savedCalculations.length > 1 && savedCalculations.length < 5) ? 'записи' : 'записей'}
        </span>
      </div>

      <div className="space-y-3">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center">
                {getCalculationTypeIcon(calc.calculationType)}
                <h4 className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  {calc.name}
                </h4>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<RefreshCw className="w-3.5 h-3.5" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<Trash2 className="w-3.5 h-3.5 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>

            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <div className="flex justify-between">
                <span>Тип:</span>
                <span className="font-medium text-cyan-600 dark:text-cyan-400">
                  {getCalculationTypeName(calc.calculationType)}
                </span>
              </div>
              
              {calc.calculationType === 'standard' && (
                <>
                  <div className="flex justify-between">
                    <span>Промежуток:</span>
                    <span>
                      {calc.results.days} дн. ({calc.results.months} мес.)
                    </span>
                  </div>
                </>
              )}
              
              {calc.calculationType === 'working' && (
                <>
                  <div className="flex justify-between">
                    <span>Рабочих дней:</span>
                    <span>
                      {calc.results.workingDays}
                    </span>
                  </div>
                </>
              )}
              
              {calc.calculationType === 'add-subtract' && (
                <>
                  <div className="flex justify-between">
                    <span>Операция:</span>
                    <span>
                      {calc.formData.operation === 'add' ? 'Добавление' : 'Вычитание'} {calc.formData.datePeriod} {calc.formData.periodType}
                    </span>
                  </div>
                </>
              )}
              
              {calc.calculationType === 'age' && (
                <>
                  <div className="flex justify-between">
                    <span>Возраст:</span>
                    <span>
                      {calc.results.years} лет {calc.results.months} мес.
                    </span>
                  </div>
                </>
              )}
              
              <div className="flex justify-between text-gray-500 dark:text-gray-500 mt-1">
                <span>Сохранено:</span>
                <span>
                  {formatSaveDate(calc.date)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedCalculationsSidebar;